import React, { useState, useEffect } from 'react';
import { useAppUser } from '../../../hooks/useAppUser';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import Divider from '@mui/material/Divider';
import Alert from '@mui/material/Alert';
import Avatar from '@mui/material/Avatar';
import CircularProgress from '@mui/material/CircularProgress';
import Snackbar from '@mui/material/Snackbar';
import MenuItem from '@mui/material/MenuItem';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import { useApi } from '../../../hooks/useApi';

function AccountSettings() {
  const { user } = useAppUser();
  const queryClient = useQueryClient();
  const api = useApi();
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  // Profile form state
  const [profile, setProfile] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.metadata?.phone || '',
  });

  // Tenant form state
  const [tenant, setTenant] = useState({
    name: user?.metadata?.organization || '',
    brandName: user?.metadata?.brandName || '',
    // Per-tenant 2FA policy (stored on the brand org in the identity system).
    // Default to on until the real value loads — see the sync effect below.
    tfaEnabled: user?.metadata?.tfaEnabled ?? true,
    // Per-tenant identity-binding policy (IF-02), same storage as 2FA.
    // 'off' is the server-side default for tenants that never opted in.
    identityBinding: user?.metadata?.identityBinding ?? 'off',
  });

  const BINDING_VALUES = ['off', 'warn', 'enforce'];
  const savedBinding = BINDING_VALUES.includes(user?.metadata?.identityBinding)
    ? user.metadata.identityBinding
    : 'off';

  // The form is seeded from `user` at mount, which can be null on a cold load;
  // reflect the server's real 2FA value once the profile query resolves (and
  // after a save invalidates + refetches it). Only syncs a concrete boolean, so
  // an in-progress toggle isn't clobbered by an unrelated refetch.
  useEffect(() => {
    const serverTfa = user?.metadata?.tfaEnabled;
    if (serverTfa === true || serverTfa === false) {
      setTenant((t) => ({ ...t, tfaEnabled: serverTfa }));
    }
  }, [user?.metadata?.tfaEnabled]);

  // Same server-sync treatment for the identity-binding policy: only a
  // concrete known value overwrites the form, so an in-progress selection
  // isn't clobbered by an unrelated refetch.
  useEffect(() => {
    const serverBinding = user?.metadata?.identityBinding;
    if (['off', 'warn', 'enforce'].includes(serverBinding)) {
      setTenant((t) => ({ ...t, identityBinding: serverBinding }));
    }
  }, [user?.metadata?.identityBinding]);

  // Password form state
  const [password, setPassword] = useState({
    current: '',
    new: '',
    confirm: '',
  });

  const updateProfileMutation = useMutation({
    mutationFn: (data) => api.put('/api/organizations/current', { profile: data }),
    onSuccess: () => {
      queryClient.invalidateQueries();
      setSuccess(true);
      setError(null);
    },
    onError: (err) => {
      setError(err.message || 'Failed to update profile');
    },
  });

  const updateTenantMutation = useMutation({
    mutationFn: (data) => api.put('/api/organizations/current', { tenant: data }),
    onSuccess: () => {
      queryClient.invalidateQueries();
      setSuccess(true);
      setError(null);
    },
    onError: (err) => {
      setError(err.message || 'Failed to update tenant');
    },
  });

  const changePasswordMutation = useMutation({
    mutationFn: (data) => api.post('/api/auth/change-password', data),
    onSuccess: () => {
      setPassword({ current: '', new: '', confirm: '' });
      setSuccess(true);
      setError(null);
    },
    onError: (err) => {
      setError(err.message || 'Failed to change password');
    },
  });

  const handleProfileSubmit = (e) => {
    e.preventDefault();
    updateProfileMutation.mutate(profile);
  };

  // Confirm before DISABLING 2FA — it's a security-reducing action for the
  // whole tenant. Enabling it (or saving other fields) needs no confirmation.
  const [confirmDisableTfaOpen, setConfirmDisableTfaOpen] = useState(false);

  // Confirm any identity-binding change involving 'enforce': switching TO it
  // can lock out apps that don't present identity tokens (fail-closed), and
  // switching AWAY from it is security-reducing. off↔warn needs no confirm —
  // warn only logs.
  const [confirmBindingChange, setConfirmBindingChange] = useState(null); // {from, to} | null

  const pendingBindingConfirm = () => {
    const to = tenant.identityBinding;
    return savedBinding !== to && (savedBinding === 'enforce' || to === 'enforce')
      ? { from: savedBinding, to }
      : null;
  };

  const handleTenantSubmit = (e) => {
    e.preventDefault();
    const currentlyEnabled = user?.metadata?.tfaEnabled !== false;
    const disablingTfa = currentlyEnabled && tenant.tfaEnabled === false;
    if (disablingTfa) {
      setConfirmDisableTfaOpen(true);
      return;
    }
    const binding = pendingBindingConfirm();
    if (binding) {
      setConfirmBindingChange(binding);
      return;
    }
    updateTenantMutation.mutate(tenant);
  };

  const confirmDisableTfa = () => {
    setConfirmDisableTfaOpen(false);
    // A single save can change both policies — chain into the binding
    // confirmation rather than skipping it.
    const binding = pendingBindingConfirm();
    if (binding) {
      setConfirmBindingChange(binding);
      return;
    }
    updateTenantMutation.mutate(tenant);
  };

  const confirmBindingSave = () => {
    setConfirmBindingChange(null);
    updateTenantMutation.mutate(tenant);
  };

  const cancelBindingSave = () => {
    setConfirmBindingChange(null);
    // Abort the save and restore the selector to the still-saved policy so
    // the UI doesn't show a mode that isn't actually active.
    setTenant((t) => ({ ...t, identityBinding: savedBinding }));
  };

  const cancelDisableTfa = () => {
    setConfirmDisableTfaOpen(false);
    // Abort the save and restore the toggle to its still-saved (enabled) state
    // so the UI doesn't show 2FA "off" while it's actually still required.
    setTenant((t) => ({ ...t, tfaEnabled: true }));
  };

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    if (password.new !== password.confirm) {
      setError('New passwords do not match');
      return;
    }
    if (password.new.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    changePasswordMutation.mutate({
      currentPassword: password.current,
      newPassword: password.new,
    });
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Account Settings
      </Typography>
      <Typography
        variant="body1"
        sx={{
          color: "text.secondary",
          mb: 4
        }}>
        Manage your account and tenant settings.
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {/* Profile Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Profile Information
        </Typography>
        <Box component="form" onSubmit={handleProfileSubmit}>
          <Grid container spacing={3}>
            <Grid
              size={{
                xs: 12,
                md: 2
              }}>
              <Avatar
                sx={{ width: 80, height: 80 }}
                src={user?.picture}
              >
                {user?.name?.[0] || user?.email?.[0]}
              </Avatar>
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 10
              }}>
              <Grid container spacing={2}>
                <Grid
                  size={{
                    xs: 12,
                    md: 6
                  }}>
                  <TextField
                    fullWidth
                    label="Name"
                    value={profile.name}
                    onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                  />
                </Grid>
                <Grid
                  size={{
                    xs: 12,
                    md: 6
                  }}>
                  <TextField
                    fullWidth
                    label="Email"
                    value={profile.email}
                    disabled
                    helperText="Email cannot be changed"
                  />
                </Grid>
                <Grid
                  size={{
                    xs: 12,
                    md: 6
                  }}>
                  <TextField
                    fullWidth
                    label="Phone"
                    value={profile.phone}
                    onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              type="submit"
              variant="contained"
              disabled={updateProfileMutation.isPending}
            >
              {updateProfileMutation.isPending ? <CircularProgress size={20} /> : 'Save Profile'}
            </Button>
          </Box>
        </Box>
      </Paper>
      {/* Tenant Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Tenant Settings
        </Typography>
        <Box component="form" onSubmit={handleTenantSubmit}>
          <Grid container spacing={2}>
            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <TextField
                fullWidth
                label="Tenant Name"
                value={tenant.name}
                onChange={(e) => setTenant({ ...tenant, name: e.target.value })}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <TextField
                fullWidth
                label="Brand Name (White-label)"
                value={tenant.brandName}
                onChange={(e) => setTenant({ ...tenant, brandName: e.target.value })}
                helperText="Displayed in your white-labeled interface"
              />
            </Grid>
          </Grid>
          <Box sx={{ mt: 3 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={tenant.tfaEnabled !== false}
                  onChange={(e) => setTenant({ ...tenant, tfaEnabled: e.target.checked })}
                />
              }
              label="Require two-factor authentication"
            />
            <Typography variant="body2" sx={{ color: 'text.secondary', ml: 6, mt: -0.5 }}>
              When on, everyone in your tenant must verify a one-time code at login.
              Turning it off removes that step for your whole organization.
            </Typography>
          </Box>
          <Box sx={{ mt: 3 }}>
            <TextField
              select
              label="User identity verification"
              value={tenant.identityBinding}
              onChange={(e) => setTenant({ ...tenant, identityBinding: e.target.value })}
              sx={{ minWidth: 280 }}
            >
              <MenuItem value="off">Off — no identity check</MenuItem>
              <MenuItem value="warn">Warn — log only, never reject</MenuItem>
              <MenuItem value="enforce">Enforce — require verified identity</MenuItem>
            </TextField>
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
              Controls whether people joining rooms must prove who they are with a
              verified sign-in, so no one can act as another user. Use &ldquo;Warn&rdquo;
              first: it only logs joins that would be rejected, letting you confirm all
              your apps pass the check before switching to &ldquo;Enforce&rdquo;.
            </Typography>
          </Box>
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              type="submit"
              variant="contained"
              disabled={updateTenantMutation.isPending}
            >
              {updateTenantMutation.isPending ? <CircularProgress size={20} /> : 'Save Tenant'}
            </Button>
          </Box>
        </Box>
      </Paper>
      {/* Security Section */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Security
        </Typography>

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle1" gutterBottom>
          Change Password
        </Typography>
        <Box component="form" onSubmit={handlePasswordSubmit}>
          <Grid container spacing={2}>
            <Grid
              size={{
                xs: 12,
                md: 4
              }}>
              <TextField
                fullWidth
                label="Current Password"
                type="password"
                value={password.current}
                onChange={(e) => setPassword({ ...password, current: e.target.value })}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 4
              }}>
              <TextField
                fullWidth
                label="New Password"
                type="password"
                value={password.new}
                onChange={(e) => setPassword({ ...password, new: e.target.value })}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 4
              }}>
              <TextField
                fullWidth
                label="Confirm New Password"
                type="password"
                value={password.confirm}
                onChange={(e) => setPassword({ ...password, confirm: e.target.value })}
              />
            </Grid>
          </Grid>
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              type="submit"
              variant="outlined"
              disabled={changePasswordMutation.isPending || !password.current || !password.new}
            >
              {changePasswordMutation.isPending ? <CircularProgress size={20} /> : 'Change Password'}
            </Button>
          </Box>
        </Box>
      </Paper>
      <Snackbar
        open={success}
        autoHideDuration={3000}
        onClose={() => setSuccess(false)}
        message="Settings saved successfully"
      />
      <Dialog open={confirmDisableTfaOpen} onClose={cancelDisableTfa}>
        <DialogTitle>Disable two-factor authentication?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This removes the one-time-code step at login for <strong>everyone in your
            tenant</strong>, lowering account security for all members. You can turn it
            back on at any time.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelDisableTfa}>Cancel</Button>
          <Button onClick={confirmDisableTfa} color="error" variant="contained">
            Disable 2FA
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={!!confirmBindingChange} onClose={cancelBindingSave}>
        <DialogTitle>
          {confirmBindingChange?.to === 'enforce'
            ? 'Enforce identity verification?'
            : 'Reduce identity verification?'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmBindingChange?.to === 'enforce' ? (
              <>
                Anyone joining through an app that does not present a verified sign-in
                identity will be <strong>unable to join rooms</strong>. Run in
                &ldquo;Warn&rdquo; mode first and confirm the logs show no would-be
                rejections before enforcing.
              </>
            ) : (
              <>
                Turning enforcement off means joins <strong>no longer require a verified
                identity</strong>, lowering protection against one user acting as
                another. You can re-enable it at any time.
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelBindingSave}>Cancel</Button>
          <Button
            onClick={confirmBindingSave}
            color={confirmBindingChange?.to === 'enforce' ? 'primary' : 'error'}
            variant="contained"
          >
            {confirmBindingChange?.to === 'enforce' ? 'Enforce' : 'Reduce'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default AccountSettings;
